/* De PDF van een revisie als Versie in de projectmap (ADR 0018).
 *
 *   1. revisie vastleggen (of de gekozen revisie zonder bestand nemen)
 *   2. PDF maken met generate-quote-pdf, van de stand die nu in de offerte staat
 *   3. naam kiezen volgens de GV-conventie (ADR 0015):
 *        GV_<locatie>_Offerte_<nummer>_v<revisie>.0_NL.pdf
 *      De eerste keer in de projectmap zelf; daarna altijd de volgende versie van het
 *      vorige gekoppelde bestand, in dezelfde map. Nooit overschrijven.
 *   4. uploaden via gv-crm-mcp (/app/begin_upload → PUT → /app/finish_upload), met de
 *      omschrijving; gv-crm-mcp schrijft project_file_versions (migratie 096)
 *   5. koppelen met link_quote_version_file (migratie 097)
 *
 * Alleen voor offertes met een project: een Kavel of Opportunity heeft geen
 * projectmap, daar blijft het een download. */

import { nextVersionName, parseFileName } from '../../../../supabase/functions/_shared/fileVersions';
import {
  haalBestanden, uploadBestand, type Bestand, type BestandenClient,
} from '../../projectpagina/data/bestanden';
import { OfferteFout, type OfferteClient } from './acties';
import { basisNummer, legRevisieVast, type Revisie, type VastlegKern } from './revisies';

export type ProjectmapClient = OfferteClient & BestandenClient;

export interface OfferteProject {
  id: string;
  name: string;
  project_number: string | null;
  sharepoint_folder_path: string | null;
}

/** Het locatiedeel van een GV-naam uit de projectnaam: 'PT-Creations — Laadinfra SPRILA' → 'PT-Creations'. */
export function locatieDeel(projectNaam: string): string {
  const eerste = projectNaam.split(/\s+[—–-]\s+/)[0] ?? projectNaam;
  const zonderNummer = eerste.replace(/^P\d{2,3}\s+/i, '');
  const schoon = zonderNummer
    .normalize('NFC')
    .replace(/\./g, '')
    .replace(/[^\p{L}\p{N}-]+/gu, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/, '');
  return schoon || 'Project';
}

const deel = (s: string) => s.normalize('NFC').replace(/[^\p{L}\p{N}-]+/gu, '-').replace(/^-+|-+$/g, '') || 'Offerte';

export const naamVoor = (locatie: string, nummer: string, revisie: number) =>
  `GV_${locatie}_Offerte_${nummer}_v${revisie}.0_NL.pdf`;

export interface BestandsPlan {
  modus: 'upload' | 'new_version';
  /** Map relatief aan de projectmap; '' is de projectmap zelf. */
  map: string;
  naam: string;
  /** Alleen bij new_version: het vorige bestand van deze offerte. */
  vorige?: string;
  versie?: string;
}

/**
 * De naam en schrijfwijze van de PDF voor `revisie`.
 * - Er is al een gekoppeld bestand: de volgende versie daarvan (versie = revisie).
 * - Anders een nieuw document in de projectmap. Staat daar al een document met dezelfde
 *   naam (een andere offerte van hetzelfde project), dan krijgt dit er een eigen naam.
 */
export function kiesBestand(input: {
  project: OfferteProject;
  quoteNumber: string;
  currentVersion: number | null;
  revisie: number;
  vorige: Pick<Revisie, 'file_name' | 'file_folder'> | null;
  bestanden: Pick<Bestand, 'name' | 'folder'>[];
}): BestandsPlan {
  const versie = `${input.revisie}.0`;
  if (input.vorige?.file_name) {
    const { name } = nextVersionName(input.vorige.file_name, { version: versie, concept: false });
    return { modus: 'new_version', map: input.vorige.file_folder ?? '', naam: name, vorige: input.vorige.file_name, versie };
  }
  const locatie = locatieDeel(input.project.name);
  const basis = deel(basisNummer(input.quoteNumber, input.currentVersion));
  const eigen = deel(input.quoteNumber);
  const inMap = new Set(
    input.bestanden
      .filter((b) => b.folder === '')
      .map((b) => parseFileName(b.name))
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
      .map((p) => p.documentKey),
  );
  const kandidaten = [basis, ...(eigen !== basis ? [eigen] : []), ...Array.from({ length: 8 }, (_, i) => `${basis}-${String.fromCharCode(66 + i)}`)];
  for (const nummer of kandidaten) {
    const naam = naamVoor(locatie, nummer, input.revisie);
    const sleutel = parseFileName(naam)?.documentKey;
    if (sleutel && !inMap.has(sleutel)) return { modus: 'upload', map: '', naam };
  }
  throw new OfferteFout('Kon geen vrije bestandsnaam vinden in de projectmap.');
}

/** De PDF zoals generate-quote-pdf hem maakt, van de stand die nu in de offerte staat. */
export async function maakPdf(client: BestandenClient, quoteId: string): Promise<Uint8Array<ArrayBuffer>> {
  const { data, error } = await client.functions.invoke('generate-quote-pdf', { body: { quoteId, includeTerms: true, optimize: 'print' } });
  if (error) throw new OfferteFout(`PDF maken mislukte: ${error.message ?? error}`);
  if (!data?.pdf) throw new OfferteFout('PDF maken mislukte: er kwam geen PDF terug.');
  const tekst = atob(data.pdf);
  const bytes = new Uint8Array(tekst.length);
  for (let i = 0; i < tekst.length; i++) bytes[i] = tekst.charCodeAt(i);
  return bytes;
}

export interface BewaarVerzoek {
  quote: VastlegKern & { updated_at?: string | null };
  project: OfferteProject;
  /** 'bestaand': de PDF hoort bij deze revisie (bevroren, zonder bestand). Anders een nieuwe revisie met dit nummer. */
  doel: { soort: 'bestaand'; revisie: Revisie } | { soort: 'nieuw'; nummer: number };
  /** Omschrijving van de Versie; wordt ook de notitie van een nieuwe revisie. */
  omschrijving: string;
  /** Het laatst gekoppelde bestand van deze offerte, als er een is. */
  vorige: Pick<Revisie, 'file_name' | 'file_folder'> | null;
}

export interface BewaarUitkomst {
  revisie: Revisie;
  naam: string;
  web_url: string | null;
}

/** Een fout na een geslaagde stap: wat al gelukt is staat in de melding. */
export class ProjectmapFout extends OfferteFout {
  stap: 'revisie' | 'pdf' | 'upload' | 'koppelen';
  constructor(stap: ProjectmapFout['stap'], message: string) {
    super(message);
    this.stap = stap;
  }
}

export const MIN_OMSCHRIJVING = 10;

export async function bewaarInProjectmap(
  client: ProjectmapClient,
  v: BewaarVerzoek,
  doeFetch: typeof fetch = fetch,
): Promise<BewaarUitkomst> {
  const omschrijving = v.omschrijving.trim();
  if (omschrijving.length < MIN_OMSCHRIJVING) {
    throw new OfferteFout(`Schrijf in een zin wat deze versie is of wat er veranderd is (minimaal ${MIN_OMSCHRIJVING} tekens).`);
  }
  if (!v.project.sharepoint_folder_path) {
    throw new OfferteFout('Dit project heeft nog geen projectmap. Koppel eerst een SharePoint-map op de projectpagina.');
  }

  // Wat er in de map staat, vóór er iets vastgelegd wordt: is gv-crm-mcp niet bereikbaar, dan stoppen we hier.
  const map = await haalBestanden(client, v.project.id, v.project.sharepoint_folder_path);
  if (map.bron !== 'sharepoint') {
    throw new OfferteFout(map.melding ?? 'De projectmap is nu niet bereikbaar; probeer het zo opnieuw.');
  }

  const revisie = v.doel.soort === 'bestaand' ? v.doel.revisie : await legRevisieVast(client, v.quote, v.doel.nummer, omschrijving);
  const opnieuw = `Revisie ${revisie.version_number} is vastgelegd; kies bij opnieuw proberen "bij revisie ${revisie.version_number}".`;

  let pdf: Uint8Array<ArrayBuffer>;
  try {
    pdf = await maakPdf(client, v.quote.id);
  } catch (e) {
    throw new ProjectmapFout('pdf', `${(e as Error).message} ${opnieuw}`);
  }

  // Het nummer kan bij het vastleggen veranderd zijn (P33 → P33-2): de naam gaat uit van wat er nu staat.
  const { data: nu } = await client.from('quotes').select('quote_number, current_version').eq('id', v.quote.id).single();
  const plan = kiesBestand({
    project: v.project,
    quoteNumber: nu?.quote_number ?? v.quote.quote_number,
    currentVersion: nu?.current_version ?? revisie.version_number,
    revisie: revisie.version_number,
    vorige: v.vorige,
    bestanden: map.bestanden,
  });

  let upload;
  try {
    upload = await uploadBestand(
      client,
      {
        projectId: v.project.id,
        map: plan.map,
        bestand: new File([pdf], plan.naam, { type: 'application/pdf' }),
        modus: plan.modus,
        vorige: plan.vorige,
        versie: plan.versie,
        concept: plan.modus === 'new_version' ? false : undefined,
        omschrijving,
      },
      doeFetch,
    );
  } catch (e) {
    throw new ProjectmapFout('upload', `Uploaden naar de projectmap mislukte: ${(e as Error).message} ${opnieuw}`);
  }

  let versieId = upload.versieId;
  if (!versieId) {
    const { data } = await client
      .from('project_file_versions')
      .select('id')
      .eq('project_id', v.project.id)
      .eq('file_name', upload.name)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    versieId = data?.id ?? null;
  }
  const staat = `De PDF staat in de projectmap (${upload.name})`;
  if (!versieId) throw new ProjectmapFout('koppelen', `${staat}, maar de versieregel ervan is niet gevonden; de koppeling aan revisie ${revisie.version_number} ontbreekt.`);

  const { data: gekoppeld, error } = await client.rpc('link_quote_version_file', {
    p_quote_version_id: revisie.id,
    p_file_version_id: versieId,
  });
  if (error) throw new ProjectmapFout('koppelen', `${staat}, maar de koppeling aan revisie ${revisie.version_number} mislukte: ${error.message}`);
  return { revisie: { ...revisie, ...(gekoppeld ?? {}) }, naam: upload.name, web_url: upload.web_url };
}
